import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { Store } from '@ngxs/store';
import {
  PaymentMethod,
  PaymentRequestStatus,
} from '../../model/payment-request/payment-request.dto';
import { SettlementService } from '../../service/settlement/settlement.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { QueryParamsService } from '../../service/query-params/query-params.service';

import {
  BankIdSignatureStatusDto,
  BankIdSignatureStatusIdentifier,
} from '../../model/payment-request/bank-id-signature-status.dto';
import { routeNames } from '../../../assets/val/route-constants';
import { AppDeepLinkService } from 'src/app/service/app-deeplink/app-deep-link.service';
import { LoggingService } from 'src/app/service/logging-service/logging.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import QRCodeStyling from 'qr-code-styling';
import { ResetBankIdSignatureStatus } from '@app/ngxs/payment-request/payment-request.actions';

@Component({
  selector: 'app-credit-settlement',
  templateUrl: './settle.component.html',
  styleUrls: ['./settle.component.css', '../page-shared.css'],
})
export class SettleComponent implements OnInit, AfterViewInit, OnDestroy {
  public paymentRequestStatus: string;
  public showStartBankIdButton: boolean;
  public signatureRequired: boolean;
  private stateSubscription: Subscription;
  public waitFor: Awaiting;

  private paymentMethod: string;

  public arr: string[] = [];
  skipUpdates: number;

  onDesktop = false;

  @ViewChild('canvas') private canvas: ElementRef;
  private qrCode = <QRCodeStyling>{};
  private qrCodeCurrentRetries = 0;
  private readonly qrCodeMaxRetries = 2;
  showQrCode = true;

  signingInitialized = false;
  signingInProgress = true;
  bankIdStatusMessage: string;
  authErrorMessageList: string[];

  private autoStartToken: string = null;

  readonly useThisDeviceButtonText = 'Öppna från denna enhet istället';
  readonly useDifferentDeviceButtonText = 'Öppna från en annan enhet';
  readonly tryAgainButtonText = 'Försök igen';

  private readonly scanQrCodeStatusMessage =
    'Starta BankID-appen i din mobil och tryck på ikonen för QR-kod. Rikta kameran mot QR-koden på den här sidan.';
  private readonly waitingMsg = 'Vänta...';

  private readonly bankIdStatusOutstandingTransaction =
    'OUTSTANDING_TRANSACTION';
  private readonly bankIdStatusStartFailed = 'START_FAILED';

  constructor(
    private store: Store,
    private settlementService: SettlementService,
    private router: Router,
    private queryParamsService: QueryParamsService,
    private deepLinkService: AppDeepLinkService,
    private loggingService: LoggingService,
    private deviceDetectorService: DeviceDetectorService,
    private location: Location
  ) {}

  ngOnInit() {
    this.bindInstanceToState();
    this.onDesktop = !this.deviceDetectorService.isMobile();
    this.showQrCode = this.onDesktop;
    this.bankIdStatusMessage = this.waitingMsg;
    this.paymentMethod = this.queryParamsService.getQueryParams().methodType;
  }

  ngAfterViewInit() {
    this.resumeMethodDependantSettlement();
    if (this.signatureRequired) {
      setTimeout(() => {
        this.showStartBankIdButton = true;
      }, 5000);
    }
  }

  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
    this.settlementService.cancelSettlement();
  }

  private resumeMethodDependantSettlement() {
    if (
      this.paymentMethod === PaymentMethod.CREDIT ||
      this.paymentMethod === PaymentMethod.INVOICE ||
      this.paymentMethod === PaymentMethod.CREDIT_STATIC_AMOUNT
    ) {
      this.signatureRequired = true;
      this.waitFor = Awaiting.BANKID_SIGNATURE;
      this.startInstallmentWithSigning();
    } else if (this.paymentMethod === PaymentMethod.TRUSTLY) {
      this.signatureRequired = false;
      this.waitFor = Awaiting.STATUS_SETTLED;
      this.settlementService.initSettlementPolling();
    } else {
      this.loggingService.warn(
        'Customer is on settle-page with unrecognized payment method type: ' +
          this.paymentMethod +
          ', polling for SETTLED status as fallback'
      );
      this.signatureRequired = false;
      this.waitFor = Awaiting.STATUS_SETTLED;
      this.settlementService.initSettlementPolling();
    }
  }

  private startInstallmentWithSigning() {
    const offerId = this.queryParamsService.getQueryParams().termId;
    const shouldOpenBankId =
      this.queryParamsService.getQueryParams().shouldOpenBankId;

    if (shouldOpenBankId === 'true') {
      if (
        this.paymentMethod === PaymentMethod.CREDIT ||
        this.paymentMethod === PaymentMethod.CREDIT_STATIC_AMOUNT
      ) {
        this.settlementService.initInstallmentsSettlement(
          offerId,
          !this.showQrCode
        );
      } else if (this.paymentMethod === PaymentMethod.INVOICE) {
        this.settlementService.initInvoiceSettlement(offerId, !this.showQrCode);
      }
    } else {
      this.settlementService.resumeBankIdSignatureSettlementPolling();
    }
  }

  private bindInstanceToState() {
    this.skipUpdates = 2;
    this.stateSubscription = this.store.subscribe((state) => {
      if (this.waitFor === Awaiting.STATUS_SETTLED) {
        if (this.skipUpdates-- > 0) {
          // on resume, store always returns old failed status first, since we haven't started polling yet
          return;
        }

        this.paymentRequestStatus = state.paymentRequest.request.status;
        if (this.paymentRequestStatus === PaymentRequestStatus.SETTLED) {
          this.handleSuccess();
        }
      } else if (this.waitFor === Awaiting.BANKID_SIGNATURE) {
        const bankIdStatus = state.paymentRequest.bankIdStatus;
        if (!bankIdStatus) {
          // the default value for the status is null, it's used before we start the polling
          return;
        }

        this.signingInitialized = true;

        if (bankIdStatus.active === false && bankIdStatus.signed === false) {
          this.handleFailure(bankIdStatus);
        } else if (bankIdStatus.signed === true) {
          this.handleSuccess();
        } else if (bankIdStatus.active === true) {
          this.handleInProgress(bankIdStatus);
        }
      }
    });
  }

  private handleSuccess() {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }

    this.router
      .navigate([routeNames.SUCCESS], { queryParamsHandling: 'preserve' })
      .then(
        () => {},
        (rejection) => {
          this.loggingService.error(
            `Navigation from ${routeNames.SETTLE} to ${routeNames.SUCCESS} was rejected.`,
            rejection
          );
        }
      );
  }

  private handleFailure(bankIdStatus: BankIdSignatureStatusDto) {
    if (
      this.showQrCode &&
      this.qrCodeCurrentRetries < this.qrCodeMaxRetries &&
      bankIdStatus.status === this.bankIdStatusStartFailed
    ) {
      this.qrCodeCurrentRetries++;
      this.settlementService.cancelSettlement();
      this.queryParamsService.setQueryParam('shouldOpenBankId', 'true');
      this.startInstallmentWithSigning();
    } else {
      this.stateSubscription.unsubscribe();
      this.stateSubscription = null;
      this.settlementService.cancelSettlement();
      this.signingInProgress = false;
      this.signingInitialized = false;
      this.qrCodeCurrentRetries = 0;
      this.authErrorMessageList = [
        'Signering misslyckades.',
        'Vänligen försök igen.',
      ];
      this.resetQrCodeComponent();
      this.store.dispatch(new ResetBankIdSignatureStatus());
    }
  }

  private handleInProgress(bankIdStatus: BankIdSignatureStatusDto) {
    if (
      this.showQrCode &&
      (bankIdStatus.status === this.bankIdStatusOutstandingTransaction ||
        bankIdStatus.status === BankIdSignatureStatusIdentifier.STARTED)
    ) {
      this.bankIdStatusMessage = this.scanQrCodeStatusMessage;
    } else if (bankIdStatus.message) {
      this.bankIdStatusMessage = bankIdStatus.message;
    }

    this.autoStartToken = bankIdStatus.autoStartToken;
    this.updateQrCode(bankIdStatus.qrCodeData);
  }

  private updateQrCode(data: string) {
    if (this.showQrCode) {
      if (!this.qrCode._options) {
        this.qrCode = new QRCodeStyling({
          width: 180,
          height: 180,
          type: 'svg',
          margin: 0,
          data: data,
          cornersSquareOptions: {
            type: 'extra-rounded',
          },
          cornersDotOptions: {
            type: 'dot',
          },
          qrOptions: {
            errorCorrectionLevel: 'L',
          },
          backgroundOptions: {
            color: 'rgba(0,0,0,0)',
          },
        });

        this.qrCode.append(this.canvas.nativeElement);
      } else {
        this.qrCode.update({ data: data });
      }
    }
  }

  public getMessage() {
    if (this.waitFor === Awaiting.STATUS_SETTLED) {
      switch (this.paymentRequestStatus) {
        case PaymentRequestStatus.OPEN:
          return 'Inväntar svar';
        case PaymentRequestStatus.PROCESSING_SIGNATURE:
          return 'Inväntar svar';
        case PaymentRequestStatus.SETTLED:
          return 'Betalningen är registrerad';
        case PaymentRequestStatus.PROPRIETARY:
          return 'Ett fel har uppstått';
        default:
          return 'Vänta...';
      }
    }
  }

  autoStartAppClick() {
    this.showQrCode = false;

    if (!this.signingInProgress) {
      this.retry();
    } else {
      this.deepLinkService.startBankId(this.autoStartToken);
    }
  }

  showQrCodeClick() {
    this.resetQrCodeComponent();

    this.showQrCode = true;

    if (!this.signingInProgress) {
      this.retry();
    }
  }

  private resetQrCodeComponent() {
    if (this.qrCode._options) {
      this.qrCode.update({ data: '' });
    }
    this.qrCode = <QRCodeStyling>{};
  }

  tryAgainClick() {
    this.retry();
  }

  private retry() {
    this.signingInProgress = true;
    this.authErrorMessageList = null;
    this.bankIdStatusMessage = this.waitingMsg;
    this.queryParamsService.setQueryParam('shouldOpenBankId', 'true');
    this.bindInstanceToState();
    this.resumeMethodDependantSettlement();
  }

  reroute(): void {
    this.location.back();
  }
}

enum Awaiting {
  BANKID_SIGNATURE = 'BANKID',
  STATUS_SETTLED = 'STATUS_SETTLED',
}
